import { useEffect, useRef, useCallback } from "react";

// Translates DOM pointer/keyboard events into remote-control messages.
// Returns canvas event handlers; attaches a window keydown listener while active.
export function useRemoteInput({ imgRef, send, controlEnabled, status }) {
  const lastMoveRef = useRef(0);

  const relCoords = useCallback((e) => {
    const el = imgRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    // The <img> fills its box (w-full h-full) but object-contain letterboxes the actual frame
    // inside it. Map the pointer onto the real displayed frame using its natural resolution,
    // so clicks stay accurate at any zoom / fullscreen / aspect ratio.
    const nw = el.naturalWidth || rect.width;
    const nh = el.naturalHeight || rect.height;
    const scale = Math.min(rect.width / nw, rect.height / nh);
    const dispW = nw * scale;
    const dispH = nh * scale;
    const offX = (rect.width - dispW) / 2;
    const offY = (rect.height - dispH) / 2;
    const x = (e.clientX - rect.left - offX) / dispW;
    const y = (e.clientY - rect.top - offY) / dispH;
    // Ignore events that land on the letterbox bars outside the actual screen image
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y };
  }, [imgRef]);

  const active = controlEnabled && status === "online";

  const onMouseMove = (e) => {
    if (!active) return;
    // Throttle to ~60Hz so we don't flood the agent with move events.
    const now = performance.now();
    if (now - lastMoveRef.current < 16) return;
    lastMoveRef.current = now;
    const c = relCoords(e);
    if (c) send({ type: "mouse_move", x: c.x, y: c.y });
  };
  const onClick = (e) => {
    if (!active) return;
    const c = relCoords(e);
    if (c) send({ type: "mouse_click", x: c.x, y: c.y, button: "left" });
  };
  const onContextMenu = (e) => {
    e.preventDefault();
    if (!active) return;
    const c = relCoords(e);
    if (c) send({ type: "mouse_click", x: c.x, y: c.y, button: "right" });
  };
  const onDoubleClick = (e) => {
    if (!active) return;
    const c = relCoords(e);
    if (c) send({ type: "mouse_double", x: c.x, y: c.y });
  };
  const onWheel = (e) => {
    if (!active) return;
    // PyAutoGUI on Windows passes this value straight through as the Windows wheel delta,
    // where WHEEL_DELTA = 120 == ONE physical wheel notch. Sending ±1 (or ±4) therefore moves
    // ~1/120th of a notch — practically nothing. Convert the browser's deltaY into notches
    // (accounting for pixel / line / page delta modes) and scale by 120 so remote scrolling
    // matches the local feel 1:1. Direction: deltaY>0 (wheel down) => scroll down (negative).
    let notches;
    if (e.deltaMode === 1) notches = e.deltaY / 3;        // line mode (~3 lines per notch)
    else if (e.deltaMode === 2) notches = e.deltaY;        // page mode
    else notches = e.deltaY / 100;                         // pixel mode (~100px per notch)
    let dy = -Math.round(notches * 120);
    if (dy === 0) return;                                  // sub-pixel jitter; ignore
    dy = Math.max(-3600, Math.min(3600, dy));              // clamp a hard flick (~30 notches)
    send({ type: "scroll", dy });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!controlEnabled || status !== "online") return;
      const target = e.target;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
      e.preventDefault();
      const key = e.key;
      const map = { " ": "space", Enter: "enter", Backspace: "backspace", Escape: "esc", Tab: "tab", ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right", Delete: "delete" };
      if (map[key]) send({ type: "key", key: map[key] });
      else if (key.length === 1) send({ type: "type", text: key });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [controlEnabled, status, send]);

  return { onMouseMove, onClick, onContextMenu, onDoubleClick, onWheel };
}
